import type { Language, OcrResult, StructuredDocument } from '../types/document'
import { isTrustedUrl } from '../shared/trusted-sources'
import { redactForNetwork } from './translate'

// Optional, user-initiated "Ask" (Poochho): a question about the document is
// answered by the Samjho Worker from the document's own text, or else only from
// official government websites. Everything else in Samjho works without this.

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '')
const MAX_DOCUMENT_CHARS = 14_000
const MAX_QUESTION_CHARS = 500

export interface AskSource {
  n: number
  url: string
  title: string
  snippet: string
}

export interface AskAnswer {
  basis: 'document' | 'web'
  text: string
  quotes: string[]
  verified: boolean
  sources: AskSource[]
}

export interface AskResult {
  answers: AskAnswer[]
  notFound: boolean
  searchedWeb: boolean
  followups: string[]
}

export interface AskTurn {
  q: string
  a: string
}

export function askConfigured(): boolean {
  return Boolean(API_BASE_URL) && import.meta.env.MODE !== 'test'
}

/**
 * The document text sent with a question: the full recognised text when we have
 * it, otherwise (saved documents keep no full text) the lines behind each fact.
 * Identifiers are removed before it leaves the phone.
 */
export function documentTextForAsk(doc: StructuredDocument, ocr: OcrResult | null): string {
  let text: string
  if (ocr && ocr.text.trim()) {
    text = ocr.text
  } else {
    const lines = [
      doc.person?.evidence,
      doc.authority?.evidence,
      doc.status?.evidence,
      doc.place?.evidence,
      ...doc.dates.map((d) => d.evidence),
      ...doc.amounts.map((a) => a.evidence),
      ...doc.references.map((r) => r.evidence),
      ...doc.actions.map((a) => a.evidence),
      ...doc.warnings.map((w) => w.evidence),
      ...doc.documentsRequired.map((d) => d.evidence),
      ...doc.contacts.map((c) => c.evidence)
    ]
    text = [...new Set(lines.filter((l): l is string => Boolean(l)))].join('\n')
  }
  return redactForNetwork(text.replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim()).slice(0, MAX_DOCUMENT_CHARS)
}

/** Official websites printed on the document (only allowlisted ones are ever read). */
export function officialWebsites(doc: StructuredDocument): string[] {
  const urls = doc.contacts
    .filter((c) => c.kind === 'website')
    .map((c) => (/^https?:\/\//i.test(c.value) ? c.value : `https://${c.value}`).replace(/[.,;)]+$/, ''))
  return [...new Set(urls.filter(isTrustedUrl))].slice(0, 4)
}

export class AskFailure extends Error {
  constructor(public reason: 'busy' | 'unavailable' | 'failed') {
    super(reason)
  }
}

export async function askQuestion(input: {
  question: string
  language: Language
  /** Null for questions from the legal help page, which have no document. */
  doc: StructuredDocument | null
  documentText: string
  topic?: 'document' | 'legal'
  history: AskTurn[]
  searchWeb: boolean
  signal?: AbortSignal
}): Promise<AskResult> {
  if (!askConfigured()) throw new AskFailure('unavailable')
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 60_000)
  input.signal?.addEventListener('abort', () => controller.abort())
  try {
    const response = await fetch(`${API_BASE_URL}/api/ask`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        question: redactForNetwork(input.question.trim()).slice(0, MAX_QUESTION_CHARS),
        language: input.language,
        docType: input.doc?.documentType ?? 'unknown',
        document: input.documentText,
        websites: input.doc ? officialWebsites(input.doc) : [],
        org: redactForNetwork(input.doc?.authority?.value ?? '').slice(0, 120),
        topic: input.topic ?? 'document',
        history: input.history.slice(-4).map((t) => ({ q: redactForNetwork(t.q), a: redactForNetwork(t.a) })),
        web: input.searchWeb ? 'auto' : 'never'
      }),
      signal: controller.signal,
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    })
    if (response.status === 429) throw new AskFailure('busy')
    if (response.status === 503 || response.status === 404) throw new AskFailure('unavailable')
    if (!response.ok) throw new AskFailure('failed')
    return sanitise(await response.json())
  } catch (error) {
    if (error instanceof AskFailure) throw error
    throw new AskFailure('failed')
  } finally {
    clearTimeout(timer)
  }
}

/** Never trust the network: keep only well-formed answers and official https(s) links. */
export function sanitise(body: unknown): AskResult {
  const b = (body ?? {}) as Partial<AskResult>
  const answers = (Array.isArray(b.answers) ? b.answers : [])
    .filter((a): a is AskAnswer => Boolean(a) && (a.basis === 'document' || a.basis === 'web') && typeof a.text === 'string' && a.text.trim().length > 0)
    .map((a) => ({
      basis: a.basis,
      text: a.text.trim(),
      quotes: Array.isArray(a.quotes) ? a.quotes.filter((q): q is string => typeof q === 'string').slice(0, 3) : [],
      verified: a.verified !== false,
      sources: (Array.isArray(a.sources) ? a.sources : [])
        .filter((s): s is AskSource => Boolean(s) && typeof s.url === 'string' && isTrustedUrl(s.url))
        .map((s) => ({ n: Number(s.n) || 0, url: s.url, title: String(s.title ?? ''), snippet: String(s.snippet ?? '') }))
    }))
    // A web answer must come with at least one official source
    .filter((a) => a.basis === 'document' || a.sources.length > 0)
  const followups = Array.isArray(b.followups) ? b.followups.filter((f): f is string => typeof f === 'string' && f.trim().length > 0).slice(0, 3) : []
  return { answers, notFound: answers.length === 0, searchedWeb: Boolean(b.searchedWeb), followups }
}

/** Answer text for reading aloud: citation markers like [1] are dropped. */
export function speakableAnswer(text: string): string {
  return text.replace(/\s*\[\d+(?:\s*,\s*\d+)*\]/g, '').replace(/\s+/g, ' ').trim()
}
